import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, Mail, Lock, User, ArrowLeft, ChefHat, Instagram, Youtube, Sparkles, Check, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import HCaptcha from '@hcaptcha/react-hcaptcha';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// TikTok icon
const TikTokIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className || "w-5 h-5"} fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

export default function AuthPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showSocialStep, setShowSocialStep] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [captchaConfig, setCaptchaConfig] = useState({ enabled: false, site_key: '' });
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);
  const { login, register } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    setMounted(true);
    // Fetch captcha config
    fetchCaptchaConfig();
  }, []);

  const fetchCaptchaConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/captcha-config`);
      const data = await res.json();
      setCaptchaConfig(data);
    } catch (err) {
      console.error('Failed to fetch captcha config:', err);
    }
  };

  const handleCaptchaVerify = (token) => {
    setCaptchaToken(token);
  };

  const handleCaptchaExpire = () => {
    setCaptchaToken(null);
  };

  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    displayName: '',
    tiktokUsername: '',
    instagramUsername: '',
    youtubeUsername: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isLogin && !showSocialStep) {
      if (!formData.username || formData.username.length < 3) {
        toast.error(t('auth.errors.usernameMin'));
        return;
      }
      if (formData.password.length < 6) {
        toast.error(t('auth.errors.passwordMin'));
        return;
      }
      // Check captcha for registration
      if (captchaConfig.enabled && !captchaToken) {
        toast.error(t('auth.errors.captchaRequired', 'Veuillez compléter le captcha'));
        return;
      }
      setShowSocialStep(true);
      return;
    }
    
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success(t('auth.welcomeBack'));
      } else {
        await register(
          formData.email, 
          formData.username, 
          formData.password, 
          formData.displayName,
          formData.tiktokUsername,
          formData.instagramUsername,
          formData.youtubeUsername,
          captchaToken
        );
        const hasSocial = formData.tiktokUsername || formData.instagramUsername || formData.youtubeUsername;
        if (hasSocial) {
          toast.success(t('auth.welcomeCreator'));
        } else {
          toast.success(t('auth.welcomeNew'));
        }
      }
      navigate('/home');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.generic'));
      // Reset captcha on error
      if (captchaRef.current) {
        captchaRef.current.resetCaptcha();
        setCaptchaToken(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSkipSocial = async () => {
    setLoading(true);
    try {
      await register(formData.email, formData.username, formData.password, formData.displayName, null, null, null, captchaToken);
      toast.success(t('auth.welcomeNew'));
      navigate('/home');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.generic'));
      // Reset captcha on error
      if (captchaRef.current) {
        captchaRef.current.resetCaptcha();
        setCaptchaToken(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: '🏆', textKey: 'landing.features.challenges' },
    { icon: '💰', textKey: 'landing.features.rewards' },
    { icon: '⭐', textKey: 'landing.features.levels' },
    { icon: '🌍', textKey: 'landing.features.community' },
  ];

  return (
    <div className="min-h-screen bg-[#F8F5F2] flex flex-col lg:flex-row">
      {/* Left Side - Branding Panel */}
      <div className={`hidden lg:flex lg:w-[45%] relative overflow-hidden transition-all duration-1000 ${mounted ? 'opacity-100' : 'opacity-0'}`}>
        {/* Background with Tunisian Pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#1E4ED8] via-[#1E4ED8] to-[#0D3A9E]">
          {/* Decorative Arches - Sidi Bou Said style */}
          <div className="absolute top-20 left-10 w-32 h-48 border-[3px] border-white/20 rounded-t-full" />
          <div className="absolute top-32 left-16 w-20 h-32 border-[2px] border-white/10 rounded-t-full" />
          <div className="absolute bottom-20 right-10 w-40 h-56 border-[3px] border-white/20 rounded-t-full" />
          <div className="absolute bottom-32 right-20 w-24 h-36 border-[2px] border-white/10 rounded-t-full" />
          
          {/* Geometric Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-0 left-0 w-full h-full" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }} />
          </div>
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center p-12 xl:p-16">
          {/* Logo */}
          <div className={`flex items-center gap-3 mb-12 transition-all duration-700 delay-200 ${mounted ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
            <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-xl">
              <span className="text-3xl">🌶️</span>
            </div>
            <span className="font-fraunces font-bold text-4xl text-white">MHARHER</span>
          </div>

          {/* Main Headline */}
          <div className={`mb-10 transition-all duration-700 delay-300 ${mounted ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
            <h1 className="font-fraunces text-4xl xl:text-5xl font-bold text-white leading-tight mb-4">
              {t('landing.title')}
              <br />
              <span className="text-[#F2A900]">{t('landing.titleHighlight')}</span>
            </h1>
            <p className="text-white/80 text-lg max-w-md leading-relaxed">
              {t('landing.subtitle')}
            </p>
          </div>

          {/* Features List */}
          <div className={`space-y-4 transition-all duration-700 delay-400 ${mounted ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="flex items-center gap-4 group"
                style={{ transitionDelay: `${500 + index * 100}ms` }}
              >
                <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center group-hover:bg-white/20 transition-colors">
                  <span className="text-xl">{feature.icon}</span>
                </div>
                <span className="text-white/90 font-medium">{t(feature.textKey)}</span>
              </div>
            ))}
          </div>

          {/* Social Proof */}
          <div className={`mt-12 pt-8 border-t border-white/10 transition-all duration-700 delay-700 ${mounted ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
            <div className="flex items-center gap-4">
              <div className="flex -space-x-3">
                {['👨‍🍳', '👩‍🍳', '🧑‍🍳', '👨‍🍳'].map((emoji, i) => (
                  <div key={i} className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border-2 border-[#1E4ED8]">
                    <span className="text-lg">{emoji}</span>
                  </div>
                ))}
              </div>
              <div>
                <p className="text-white font-semibold">{t('landing.socialProof')}</p>
                <p className="text-white/60 text-sm">{t('landing.socialProofSub')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Form Panel */}
      <div className={`flex-1 flex flex-col transition-all duration-700 ${mounted ? 'opacity-100' : 'opacity-0'}`}>
        {/* Mobile Header */}
        <div className="lg:hidden bg-gradient-to-r from-[#1E4ED8] to-[#0D3A9E] px-6 py-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <span className="text-2xl">🌶️</span>
            </div>
            <span className="font-fraunces font-bold text-3xl text-white">MHARHER</span>
          </div>
          <p className="text-white/80">{t('landing.title')} {t('landing.titleHighlight')}</p>
        </div>

        {/* Form Container */}
        <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
          <div className="w-full max-w-md">
            {/* Back Button */}
            <button
              onClick={() => showSocialStep ? setShowSocialStep(false) : navigate('/')}
              className="flex items-center gap-2 text-gray-500 hover:text-[#1E4ED8] mb-8 transition-colors group"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              {showSocialStep ? t('common.back') : t('common.home')}
            </button>

            {/* Social Links Step */}
            {showSocialStep ? (
              <div className="animate-fade-in-up">
                <div className="mb-8">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#F2A900]/10 rounded-full mb-4">
                    <Sparkles className="w-4 h-4 text-[#F2A900]" />
                    <span className="text-sm font-medium text-[#F2A900]">{t('auth.step')} 2/2</span>
                  </div>
                  <h2 className="font-fraunces text-3xl font-bold text-gray-900 mb-2">
                    {t('auth.areYouCreator')}
                  </h2>
                  <p className="text-gray-500">
                    {t('auth.addSocialMedia')}
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* TikTok */}
                  <div className="space-y-2">
                    <Label htmlFor="tiktok" className="flex items-center gap-2 text-gray-700">
                      <TikTokIcon className="w-4 h-4" />
                      TikTok
                    </Label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-medium">@</span>
                      <Input
                        id="tiktok"
                        data-testid="tiktok-input"
                        type="text"
                        placeholder="ton_username"
                        className="pl-10 h-12 rounded-xl border-2 border-gray-200 focus:border-[#1E4ED8] transition-colors"
                        value={formData.tiktokUsername}
                        onChange={(e) => setFormData({ ...formData, tiktokUsername: e.target.value.replace('@', '') })}
                      />
                    </div>
                  </div>

                  {/* Instagram */}
                  <div className="space-y-2">
                    <Label htmlFor="instagram" className="flex items-center gap-2 text-gray-700">
                      <Instagram className="w-4 h-4 text-pink-500" />
                      Instagram
                    </Label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-medium">@</span>
                      <Input
                        id="instagram"
                        data-testid="instagram-input"
                        type="text"
                        placeholder="ton_username"
                        className="pl-10 h-12 rounded-xl border-2 border-gray-200 focus:border-[#1E4ED8] transition-colors"
                        value={formData.instagramUsername}
                        onChange={(e) => setFormData({ ...formData, instagramUsername: e.target.value.replace('@', '') })}
                      />
                    </div>
                  </div>

                  {/* YouTube */}
                  <div className="space-y-2">
                    <Label htmlFor="youtube" className="flex items-center gap-2 text-gray-700">
                      <Youtube className="w-4 h-4 text-red-500" />
                      YouTube <span className="text-gray-400 text-xs">(optionnel)</span>
                    </Label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-medium">@</span>
                      <Input
                        id="youtube"
                        data-testid="youtube-input"
                        type="text"
                        placeholder="ta_chaine"
                        className="pl-10 h-12 rounded-xl border-2 border-gray-200 focus:border-[#1E4ED8] transition-colors"
                        value={formData.youtubeUsername}
                        onChange={(e) => setFormData({ ...formData, youtubeUsername: e.target.value.replace('@', '') })}
                      />
                    </div>
                  </div>

                  {/* Info box */}
                  <div className="bg-gradient-to-br from-[#1E4ED8]/5 to-[#60A5FA]/5 border border-[#1E4ED8]/20 rounded-2xl p-5">
                    <p className="font-semibold text-[#1E4ED8] mb-3 flex items-center gap-2">
                      <ChefHat className="w-5 h-5" />
                      {t('auth.creatorBenefits')}
                    </p>
                    <ul className="text-gray-600 space-y-2 text-sm">
                      {[
                        t('auth.benefits.badge'),
                        t('auth.benefits.priority'),
                        t('auth.benefits.visibility'),
                        t('auth.benefits.partnerships')
                      ].map((item, i) => (
                        <li key={i} className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-[#4F6F52]" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Submit Buttons */}
                  <div className="space-y-3 pt-2">
                    <Button
                      type="submit"
                      data-testid="auth-submit-btn"
                      className="w-full h-14 text-lg bg-gradient-to-r from-[#1E4ED8] to-[#0D3A9E] hover:from-[#1842B8] hover:to-[#0A2D7A] rounded-xl shadow-lg shadow-[#1E4ED8]/30 transition-all hover:shadow-xl hover:shadow-[#1E4ED8]/40"
                      disabled={loading}
                    >
                      {loading ? (
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          {t('common.loading')}
                        </div>
                      ) : (
                        <>
                          <ChefHat className="mr-2 w-5 h-5" />
                          {t('auth.createCreatorAccount')}
                        </>
                      )}
                    </Button>
                    
                    <button
                      type="button"
                      onClick={handleSkipSocial}
                      disabled={loading}
                      className="w-full py-3 text-gray-500 hover:text-[#1E4ED8] transition-colors font-medium"
                      data-testid="skip-social-btn"
                    >
                      {t('auth.skipStep')} →
                    </button>
                  </div>
                </form>
              </div>
            ) : (
              /* Login/Register Form */
              <div className="animate-fade-in-up">
                {/* Toggle Tabs */}
                <div className="flex bg-gray-100 rounded-xl p-1 mb-8">
                  <button
                    type="button"
                    onClick={() => setIsLogin(true)}
                    className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
                      isLogin 
                        ? 'bg-white text-[#1E4ED8] shadow-sm' 
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    data-testid="login-tab"
                  >
                    {t('auth.login')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsLogin(false)}
                    className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
                      !isLogin 
                        ? 'bg-white text-[#1E4ED8] shadow-sm' 
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    data-testid="register-tab"
                  >
                    {t('auth.register')}
                  </button>
                </div>

                {/* Title */}
                <div className="mb-8">
                  <h2 className="font-fraunces text-3xl font-bold text-gray-900 mb-2">
                    {isLogin ? t('auth.welcomeBackTitle') : t('auth.joinTitle')}
                  </h2>
                  <p className="text-gray-500">
                    {isLogin
                      ? t('auth.loginSubtitle')
                      : t('auth.registerSubtitle')}
                  </p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-5">
                  {!isLogin && (
                    <div className="space-y-5">
                      {/* Username */}
                      <div className="space-y-2">
                        <Label htmlFor="username" className="text-gray-700 font-medium">{t('auth.username')}</Label>
                        <div className="relative">
                          <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <Input
                            id="username"
                            data-testid="username-input"
                            type="text"
                            placeholder="chef_mehdi"
                            className="pl-12 h-12 rounded-xl border-2 border-gray-200 focus:border-[#1E4ED8] transition-colors"
                            value={formData.username}
                            onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase().replace(/\s/g, '_') })}
                            required={!isLogin}
                          />
                        </div>
                        <p className="text-xs text-gray-400 ml-1">mharher.app/@{formData.username || 'toi'}</p>
                      </div>

                      {/* Display Name */}
                      <div className="space-y-2">
                        <Label htmlFor="displayName" className="text-gray-700 font-medium">{t('auth.displayName')}</Label>
                        <div className="relative">
                          <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <Input
                            id="displayName"
                            data-testid="display-name-input"
                            type="text"
                            placeholder="Chef Mehdi"
                            className="pl-12 h-12 rounded-xl border-2 border-gray-200 focus:border-[#1E4ED8] transition-colors"
                            value={formData.displayName}
                            onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Email */}
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-gray-700 font-medium">{t('auth.email')}</Label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        id="email"
                        data-testid="email-input"
                        type="email"
                        placeholder="chef@mharher.tn"
                        className="pl-12 h-12 rounded-xl border-2 border-gray-200 focus:border-[#1E4ED8] transition-colors"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  {/* Password */}
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-gray-700 font-medium">{t('auth.password')}</Label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        id="password"
                        data-testid="password-input"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        className="pl-12 pr-12 h-12 rounded-xl border-2 border-gray-200 focus:border-[#1E4ED8] transition-colors"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#1E4ED8] transition-colors"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  {/* Forgot Password - Login only */}
                  {isLogin && (
                    <div className="text-right">
                      <button type="button" className="text-sm text-[#1E4ED8] hover:underline font-medium">
                        {t('auth.forgotPassword')}
                      </button>
                    </div>
                  )}

                  {/* hCaptcha - Registration only */}
                  {!isLogin && captchaConfig.enabled && captchaConfig.site_key && (
                    <div className="flex flex-col items-center gap-2">
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <ShieldCheck className="w-4 h-4 text-green-500" />
                        <span>{t('auth.captchaProtection', 'Protection anti-robot')}</span>
                      </div>
                      <HCaptcha
                        ref={captchaRef}
                        sitekey={captchaConfig.site_key}
                        onVerify={handleCaptchaVerify}
                        onExpire={handleCaptchaExpire}
                        theme="light"
                      />
                      {captchaToken && (
                        <div className="flex items-center gap-1 text-sm text-green-600">
                          <Check className="w-4 h-4" />
                          <span>{t('auth.captchaVerified', 'Vérifié')}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    data-testid="auth-submit-btn"
                    className="w-full h-14 text-lg bg-gradient-to-r from-[#1E4ED8] to-[#0D3A9E] hover:from-[#1842B8] hover:to-[#0A2D7A] rounded-xl shadow-lg shadow-[#1E4ED8]/30 transition-all hover:shadow-xl hover:shadow-[#1E4ED8]/40"
                    disabled={loading}
                  >
                    {loading ? (
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        {isLogin ? t('common.loading') : t('common.loading')}
                      </div>
                    ) : (
                      <>
                        <ChefHat className="mr-2 w-5 h-5" />
                        {isLogin ? t('auth.loginButton') : t('auth.continueButton')}
                      </>
                    )}
                  </Button>
                </form>

                {/* Divider */}
                <div className="relative my-8">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-[#F8F5F2] text-gray-500">{t('auth.orContinueWith')}</span>
                  </div>
                </div>

                {/* Social Login Buttons */}
                <div className="grid grid-cols-2 gap-3">
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/auth/oauth/google/login`}
                    className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 border-gray-200 hover:border-[#1E4ED8] hover:bg-[#1E4ED8]/5 transition-all font-medium text-gray-700"
                    data-testid="google-login-btn"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Google
                  </a>
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/auth/oauth/facebook/login`}
                    className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 border-gray-200 hover:border-[#1E4ED8] hover:bg-[#1E4ED8]/5 transition-all font-medium text-gray-700"
                    data-testid="facebook-login-btn"
                  >
                    <svg className="w-5 h-5 text-[#1877F2]" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.477 2 2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.879V14.89h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.989C18.343 21.129 22 16.99 22 12c0-5.523-4.477-10-10-10z"/>
                    </svg>
                    Facebook
                  </a>
                </div>

                {/* Terms */}
                {!isLogin && (
                  <p className="mt-8 text-xs text-center text-gray-400">
                    {t('auth.terms.text')}{' '}
                    <a href="#" className="text-[#1E4ED8] hover:underline">{t('auth.terms.conditions')}</a>
                    {' '}{t('auth.terms.and')}{' '}
                    <a href="#" className="text-[#1E4ED8] hover:underline">{t('auth.terms.privacy')}</a>
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 text-center text-sm text-gray-400">
          {t('app.copyright')}
        </div>
      </div>
    </div>
  );
}
